import { DragDropProvider as DndKitDragDropProvider, useDragOperation } from '@dnd-kit/react';

import { ReactNode } from 'react';
import { move } from '@dnd-kit/helpers';
import { useSortable } from '@dnd-kit/react/sortable';

export type DragItem = {
  id: string;
  // Items can only be reordered against other items sharing the same group (e.g. category).
  group: string;
};

export type DragDropProviderProps = {
  items: DragItem[];
  onReorder: (items: DragItem[]) => void;
  children: ReactNode;
};

export const DragDropProvider = ({ items, onReorder, children }: DragDropProviderProps): JSX.Element => (
  <DndKitDragDropProvider
    onDragEnd={(event): void => {
      if (event.canceled || !event.operation.target) return;
      onReorder(move(items, event));
    }}
  >
    {children}
  </DndKitDragDropProvider>
);

export type UseDragItemResult = {
  ref: (element: Element | null) => void;
  handleRef: (element: Element | null) => void;
  isDragging: boolean;
};

export const useDragItem = (item: DragItem, index: number, disabled?: boolean): UseDragItemResult => {
  const { ref, handleRef, isDragging } = useSortable<DragItem>({
    id: item.id,
    index,
    data: item,
    disabled,
    accept: (source) => source.data?.group === item.group,
  });

  return { ref, handleRef, isDragging };
};

// The group of the item currently being dragged, or undefined when no drag is in progress.
// Lets rows outside that group visually indicate they aren't valid drop targets.
export const useDraggingGroup = (): string | undefined => {
  const { source } = useDragOperation<DragItem>();
  return source?.data.group;
};
